import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import { Button, Card, CardBody, Col, Form, Row } from "react-bootstrap";
import * as yup from "yup";
import { IMaterialType } from "../../shared/interfaces/material-type/IMaterialType";
import { MaterialTypeService } from "./service";
import { useUnsavedChanges } from "../../shared/hooks/useUnsavedChanges";

const schema = yup.object().shape({
    name: yup.string().required("validation.required"),
    isLabor: yup.boolean(),
});

const MaterialTypeForm = () => {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const isEdit = Boolean(id);
    const navigate = useNavigate();
    const {
        control,
        handleSubmit,
        reset,
        formState: { errors, isDirty },
    } = useForm<IMaterialType>({
        resolver: yupResolver(schema) as any,
        defaultValues: isEdit
            ? async () => {
                try {
                    return await MaterialTypeService.getSingle(+id!);
                } catch (e) {
                    console.error(e);
                }
            }
            : {
                name: "",
                isLabor: false,
            },
    });

    const { guardedNavigate, unsavedChangesModal } = useUnsavedChanges(isDirty);

    const onSubmit = async (data: IMaterialType) => {
        try {
            if (isEdit) {
                await MaterialTypeService.update(data, +id!);
            } else {
                await MaterialTypeService.create(data);
            }
            SuccessToast(t(`materialType.${isEdit ? "successUpdate" : "successCreate"}`));
            navigate("/material-types");
            reset();
        } catch (error) {
            ErrorToast(error);
        }
    };

    return (
        <React.Fragment>
            <Row>
                <Col sm={12}>
                    <Card>
                        <CardBody>
                            <Form onSubmit={handleSubmit(onSubmit)}>
                                <Row className="g-3">
                                    <Col sm={12} xl={6}>
                                        <Form.Label className={"required"}>{t("materialType.name")}</Form.Label>
                                        <Controller
                                            name="name"
                                            control={control}
                                            render={({ field, fieldState }) => (
                                                <>
                                                    <Form.Control
                                                        type="text"
                                                        isInvalid={!!fieldState.error}
                                                        placeholder={t("materialType.enterName")}
                                                        {...field}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {t(fieldState.error?.message as string)}
                                                    </Form.Control.Feedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col sm={12} xl={6}>
                                        <Form.Label>{t("materialType.isLabor")}</Form.Label>
                                        <Controller
                                            name="isLabor"
                                            control={control}
                                            render={({ field }) => (
                                                <Form.Check
                                                    type="switch"
                                                    id="isLabor-switch"
                                                    checked={field.value ?? false}
                                                    onChange={(e) => field.onChange(e.target.checked)}
                                                />
                                            )}
                                        />
                                    </Col>
                                    <Col sm={12} className="d-flex gap-2">
                                        <Button type="submit" variant="primary">
                                            {t(`materialType.${isEdit ? "update" : "create"}`)}
                                        </Button>
                                        <Button type="button" variant="secondary" onClick={() => guardedNavigate("/material-types")}>
                                            {t("button.cancel")}
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            {unsavedChangesModal}
        </React.Fragment>
    );
};

export default MaterialTypeForm;
